package br.com.texeratx.boardgametimeapp.ui.screens.matches.edit

import androidx.lifecycle.ViewModel
import br.com.texeratx.boardgametimeapp.core.i18n.LanguageManager
import androidx.lifecycle.viewModelScope
import br.com.texeratx.boardgametimeapp.data.model.match.UpdateMatchRequest
import br.com.texeratx.boardgametimeapp.data.model.match.UpdateMatchScoreRequest
import br.com.texeratx.boardgametimeapp.data.repository.ExpansionRepository
import br.com.texeratx.boardgametimeapp.data.repository.MatchRepository
import br.com.texeratx.boardgametimeapp.data.repository.PlayerRepository
import br.com.texeratx.boardgametimeapp.ui.screens.expansions.model.ExpansionUI
import br.com.texeratx.boardgametimeapp.ui.screens.matches.model.MatchListItemUI
import br.com.texeratx.boardgametimeapp.ui.screens.matches.model.MatchPlayerUI
import br.com.texeratx.boardgametimeapp.ui.screens.players.model.PlayerUI
import br.com.texeratx.boardgametimeapp.ui.utils.apiToUiDate
import br.com.texeratx.boardgametimeapp.ui.utils.extractDatePartApi
import br.com.texeratx.boardgametimeapp.ui.utils.extractTimePart
import br.com.texeratx.boardgametimeapp.ui.utils.uiToApiDateTime
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class MatchEditViewModel(
    private val matchRepository: MatchRepository,
    private val playersRepository: PlayerRepository,
    private val expansionRepository: ExpansionRepository,
    private val lang: LanguageManager
) : ViewModel() {

    private val _state = MutableStateFlow<MatchEditUiState?>(null)
    val state = _state.asStateFlow()

    // Mantém referência do match original (imutável)
    private lateinit var originalMatch: MatchListItemUI

    /* ==========================================================
     * INIT
     * ========================================================== */

    fun init(match: MatchListItemUI) {
        originalMatch = match

        val datePart = extractDatePartApi(match.date)
        val timePart = extractTimePart(match.date)

        _state.value = MatchEditUiState(
            date = apiToUiDate(datePart).let { if (it == "-") "" else it }, // ✅ dd/MM/yyyy
            time = timePart, // ✅ HH:mm
            duration = match.duration.toString(),
            observation = null,
            players = match.players.map {
                EditablePlayerScoreUI(
                    playerId = it.id,
                    name = it.name,
                    nickname = it.nickname,
                    imageUrl = it.imageUrl,
                    points = it.points.toInt().toString()
                )
            },
            selectedExpansionIds = match.expansionIds
        )

        loadExpansions(match.game.id)
    }

    /* ==========================================================
     * FIELD UPDATES
     * ========================================================== */

    fun updateDate(value: String) {
        _state.update { it?.copy(date = value) }
    }

    fun updateTime(value: String) {
        _state.update { it?.copy(time = value) }
    }

    fun updateDuration(value: String) {
        _state.update { it?.copy(duration = value) }
    }

    fun updateObservation(value: String) {
        _state.update { it?.copy(observation = value) }
    }

    fun updatePlayerScore(playerId: Long, value: String) {
        _state.update {
            it?.copy(
                players = it.players.map { p ->
                    if (p.playerId == playerId) p.copy(points = value) else p
                }
            )
        }
    }

    fun removePlayer(playerId: Long) {
        _state.update {
            it?.copy(players = it.players.filterNot { p -> p.playerId == playerId })
        }
    }

    /* ==========================================================
     * ADD PLAYER FLOW
     * ========================================================== */

    fun openAddPlayerDialog() {
        _state.update { it?.copy(isAddPlayerDialogOpen = true) }
        loadAvailablePlayers()
    }

    fun closeAddPlayerDialog() {
        _state.update { it?.copy(isAddPlayerDialogOpen = false) }
    }

    private fun loadAvailablePlayers() {
        val current = _state.value ?: return

        viewModelScope.launch {
            _state.update { it?.copy(isLoadingPlayers = true) }

            playersRepository.getPlayers()
                .onSuccess { players ->
                    val alreadyAdded = current.players.map { it.playerId }.toSet()

                    _state.update {
                        it?.copy(
                            availablePlayers = players
                                .filterNot { alreadyAdded.contains(it.id) }
                                .map { p ->
                                    PlayerUI(
                                        id = p.id,
                                        name = p.name,
                                        nickname = p.nickname,
                                        imageUrl = p.imageUrl,
                                        birthday = p.birthday,
                                        phone = p.phone,
                                        clanId = p.clanId
                                    )
                                },
                            isLoadingPlayers = false
                        )
                    }
                }
                .onFailure {
                    _state.update { it?.copy(isLoadingPlayers = false) }
                }
        }
    }

    fun addPlayer(player: PlayerUI) {
        _state.update {
            it?.copy(
                players = it.players + EditablePlayerScoreUI(
                    playerId = player.id,
                    name = player.name,
                    nickname = player.nickname,
                    imageUrl = player.imageUrl,
                    points = ""
                ),
                isAddPlayerDialogOpen = false
            )
        }
    }

    /* ==========================================================
     * EXPANSIONS
     * ========================================================== */

    private fun loadExpansions(gameId: Long) {
        viewModelScope.launch {
            _state.update { it?.copy(isLoadingExpansions = true) }

            expansionRepository.getExpansionsByGame(gameId)
                .onSuccess { expansions ->
                    _state.update {
                        it?.copy(
                            availableExpansions = expansions.map {
                                ExpansionUI(
                                    id = it.id,
                                    name = it.name,
                                    imageUrl = it.imageUrl
                                )
                            },
                            isLoadingExpansions = false
                        )
                    }
                }
                .onFailure {
                    _state.update { it?.copy(isLoadingExpansions = false) }
                }
        }
    }

    fun toggleExpansion(expansionId: Long) {
        _state.update {
            it?.copy(
                selectedExpansionIds =
                    if (it.selectedExpansionIds.contains(expansionId)) {
                        it.selectedExpansionIds - expansionId
                    } else {
                        it.selectedExpansionIds + expansionId
                    }
            )
        }
    }

    /* ==========================================================
     * SAVE
     * ========================================================== */

    fun save(
        onSuccess: (MatchListItemUI) -> Unit
    ) {
        val current = _state.value ?: return

        val scores = current.players.mapNotNull { p ->
            p.points.toDoubleOrNull()?.let {
                UpdateMatchScoreRequest(
                    playerId = p.playerId,
                    points = it
                )
            }
        }

        // 🔒 validações
        if (scores.isEmpty() || current.players.isEmpty()) {
            _state.update { it?.copy(error = lang.strings.value.invalidPlayersAndScores) }
            return
        }

        viewModelScope.launch {
            _state.update { it?.copy(isSaving = true, error = null) }

            val apiDateTime = uiToApiDateTime(current.date, current.time)
            if (apiDateTime == null) {
                _state.update { it?.copy(error = lang.strings.value.invalidDateFormat) }
                return@launch
            }

            val body = UpdateMatchRequest(
                date = apiDateTime, // ✅ yyyy-MM-dd'T'HH:mm
                duration = current.duration.toIntOrNull() ?: 0,
                observation = current.observation,
                scores = scores,
                clanExpansionIds = current.selectedExpansionIds
            )

            matchRepository.updateMatch(originalMatch.id, body)
                .onSuccess {

                    // 🔁 Reconstrói o MatchListItemUI localmente
                    val updatedMatch = MatchListItemUI(
                        id = originalMatch.id,
                        date = apiDateTime,
                        duration = body.duration,
                        game = originalMatch.game,

                        players = current.players.map { editable ->
                            MatchPlayerUI(
                                id = editable.playerId,
                                name = editable.name,
                                nickname = editable.nickname,
                                imageUrl = editable.imageUrl,
                                points = editable.points.toDoubleOrNull() ?: 0.0
                            )
                        },

                        expansionIds = current.selectedExpansionIds
                    )

                    _state.update { it?.copy(isSaving = false) }
                    onSuccess(updatedMatch)
                }
                .onFailure { e ->
                    _state.update {
                        it?.copy(
                            isSaving = false,
                            error = e.message ?: lang.strings.value.errorSavingMatch
                        )
                    }
                }
        }
    }
}
